import React from 'react'
import { RadarComponent } from '../../components/Charts/Radar.tsx'
import PeriodAssessmentsChart from '../../components/Charts/PeriodAssessmentsChart.tsx'
import Loader from '../../common/Loader'
import { getMe } from '../../MeProvider.ts'
import OverviewCard from './OverviewCard.tsx'
import { formatTimestamp, snakeToWords, toSnakeCase } from './utils.ts'
import dayjs from 'dayjs'
import { useGetAssessment, useGetFullTrajectory, useGetUserPositionAssessments } from '../../http/hooks.ts'
import { IAssessment, IUser } from '../../services/nextpeakClient'
import { calculateAverageScore, generateChartData } from '../Assessments/utils.ts'
import { getEmployeeResponse, getManagerResponse } from '../../utils.ts'
import { Page } from '../../layout/Page.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import TrajectoryCounter from '../../newComponents/TrajectoryCounter/index.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { Heading3, Heading2 } from '../../newComponents/Headings'
import { Text } from '../../newComponents/Texts'

const PositionAssessmentDisplay = ({ assessments }: { assessments: IAssessment[] }) => {
	const mappedData = assessments?.reduce(
		(acc, assessment) => {
			if (assessment) {
				const managerResponse = getManagerResponse(assessment)
				const employeeResponse = getEmployeeResponse(assessment)
				const timestamp = dayjs(managerResponse!.completedAt).valueOf()
				acc.manager.push([timestamp, calculateAverageScore(managerResponse!.answers!)])
				acc.employee.push([timestamp, calculateAverageScore(employeeResponse!.answers!)])
			}

			return acc
		},
		{
			manager: [] as number[][],
			employee: [] as number[][],
		}
	)

	return (
		<PeriodAssessmentsChart
			series={[
				{ name: 'Manager eval %', data: mappedData!.manager },
				{
					name: 'Personal eval %',
					data: mappedData!.employee,
				},
			]}
		/>
	)
}

export const PositionAssessments = ({ data, isLoading }: { data?: IAssessment[]; isLoading: boolean }) => {
	const dataExists = Boolean(data?.length)

	return (
		<Card className="col-span-12 xl:col-span-8">
			<Heading3>Position progress</Heading3>
			<div style={{ minHeight: '414px' }} className={!dataExists ? 'flex justify-center items-center' : 'mt-6'}>
				{isLoading && <Loader />}
				{!isLoading && dataExists && <PositionAssessmentDisplay assessments={data!} />}
				{!isLoading && !dataExists && <p className="text-black dark:text-white">No data yet</p>}
			</div>
		</Card>
	)
}

const LatestAssessmentGraph = ({ data }: { data: IAssessment }) => {
	const chartData = generateChartData(data)

	return <RadarComponent chartData={chartData!} />
}

export const LatestAssessment = ({ data: allAssessments }: { data?: IAssessment[]; isLoading: boolean }) => {
	const nextPeakClient = useNextPeakClient()
	const { data, isLoading } = useGetAssessment(
		nextPeakClient,
		{
			assessmentId: allAssessments?.[0]?.id!,
			includes: ['assessmentResponses', 'assessmentResponses.answers', 'assessmentResponses.answers.question'],
		},
		{ enabled: !!allAssessments?.[0]?.id }
	)

	return (
		<Card className="col-span-12 xl:col-span-4">
			<Heading3>Current evaluation</Heading3>
			<div style={{ minHeight: '404px' }} className="flex justify-center items-center">
				{isLoading && <Loader />}
				{!isLoading && data && <LatestAssessmentGraph data={data} />}
				{!isLoading && !data && <p className="text-black dark:text-white">No data</p>}
			</div>
		</Card>
	)
	// return <RadarComponent chartData={chartData}/>
}

const HomePage: React.FC = () => {
	const me = getMe()
	const nextPeakClient = useNextPeakClient()
	const { data, isLoading } = useGetUserPositionAssessments(nextPeakClient, {
		userId: me.id!,
		filters: {
			latest: 0,
			isCompleted: 1,
			includes: ['assessmentResponses', 'assessmentResponses.answers'],
		},
	})
	const { data: trajectory, isLoading: isLoadingTrajectory } = useGetFullTrajectory(nextPeakClient, {
		path: toSnakeCase(me.userPosition!.positionTrajectory!.fromPosition!.role) + '_path',
	})

	return (
		<Page title="Admin">
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
				{
					<OverviewCard title="Current position level" total={me?.userPosition!.positionTrajectory!.fromPosition!.level ?? 'Unknown level'}>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#3C50E0" className="size-6">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
							/>
						</svg>
					</OverviewCard>
				}
				<div className="col-span-2 rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] py-6 px-7.5">
					{isLoadingTrajectory && <Loader />}
					{!isLoadingTrajectory && trajectory && <TrajectoryCounter employee={me as unknown as IUser} trajectoryItems={trajectory} />}

					<div className="mt-4 flex items-end justify-between">
						<div>
							<Heading2 size="lg">
								{me?.userPosition!.positionTrajectory!.path ? snakeToWords(me.userPosition!.positionTrajectory!.path) : 'Unknown path'}
							</Heading2>
							<Text size='sm' weight='medium'>Current career path</Text>
						</div>
					</div>
				</div>
				<OverviewCard
					title="At position since"
					total={me.userPosition?.sinceDate ? formatTimestamp(me.userPosition.sinceDate, 'DD MMM YYYY') : ''}
				>
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#3C50E0" className="size-6">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
						/>
					</svg>
				</OverviewCard>
			</div>
			<div className="mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6 2xl:mt-7.5 2xl:gap-7.5">
				<PositionAssessments data={data} isLoading={isLoading} />
				<LatestAssessment data={data} isLoading={isLoading} />
			</div>
		</Page>
	)
}

export default HomePage
